import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

/* ─── Persona data ─────────────────────────────────────────────────────── */
const PERSONAS = {
  parent: {
    headline: "Run a real business from home — on your schedule.",
    copy: "You already manage a household. You already organise everyone around you. KYo.Own lets you turn that skill into income — without leaving home, without a boss, and without start-up costs that don't make sense.",
    quote: '"I do school runs in the morning, KYo.Own pickups in the afternoon. Nobody clocks me in or out."',
    stat: "JA$8,000–15,000",
    statLabel: "Illustrative weekly earnings, part-time, 15–25 deliveries*",
  },
  driver: {
    headline: "Same roads. Now every stop pays twice.",
    copy: "You already know every street, every neighbourhood, every community hotspot. KYo.Own turns your existing route and local knowledge into a second income stream — with a real business name and app behind you.",
    quote: '"Same roads I already drive every day. Now every stop pays twice."',
    stat: "JA$14,000–28,000",
    statLabel: "Illustrative weekly earnings, 30–50 deliveries alongside route work*",
  },
  teacher: {
    headline: "Your reputation already opens doors. Use it after hours.",
    copy: "Hundreds of people trust your name. KYo.Own lets you build a supplementary income by offering your community a courier service they can rely on — evenings and weekends, without leaving your profession.",
    quote: '"Parents already trust me with their kids. Trusting me with a parcel wasn\'t a stretch."',
    stat: "JA$6,000–12,000",
    statLabel: "Illustrative weekly earnings, evenings and weekends*",
  },
  creator: {
    headline: "Turn your followers into customers.",
    copy: "You already have reach. KYo.Own gives you a product your audience actually needs — and a real business model to monetise that relationship. Start earning from your community in under 24 hours.",
    quote: '"I posted once. My comments turned into my first ten customers."',
    stat: "JA$10,000–25,000+",
    statLabel: "Illustrative weekly earnings, scales with audience size*",
  },
  retiree: {
    headline: "Stay active. Stay earning. Own your time.",
    copy: "You have experience, community respect, and time. KYo.Own is a low-stress business you can run at whatever pace suits you — with full support and a platform that does the hard work for you.",
    quote: '"I wasn\'t ready to sit still. Now I have somewhere to be and something to show for it."',
    stat: "JA$5,000–10,000",
    statLabel: "Illustrative weekly earnings, light part-time pace*",
  },
  beauty: {
    headline: "You already see them every week. Now deliver for them too.",
    copy: "Your clients sit in your chair for an hour at a time — that's a relationship most businesses would pay for. KYo.Own lets you offer them a courier service between appointments, using trust you've already built.",
    quote: '"My clients already trust me with their hair. Now I deliver their packages too."',
    stat: "JA$7,000–14,000",
    statLabel: "Illustrative weekly earnings, built around your client book*",
  },
};

const PERSONA_TABS = [
  { key: 'parent',  label: 'Stay-at-home parent' },
  { key: 'driver',  label: 'Taxi / route driver' },
  { key: 'teacher', label: 'Teacher / civil servant' },
  { key: 'creator', label: 'Young entrepreneur' },
  { key: 'retiree', label: 'Retiree' },
  { key: 'beauty',  label: 'Hairdresser / nail tech' },
];

/* ─── Shared section header ────────────────────────────────────────────── */
function SectionHead({ eyebrow, heading, sub, light = false }) {
  return (
    <div className="mb-10">
      {eyebrow && (
        <p className={`text-[13px] font-bold uppercase tracking-widest mb-3 ${light ? 'text-red-300' : 'text-[#ED2E24]'}`}>
          {eyebrow}
        </p>
      )}
      <h2 className={`text-3xl sm:text-4xl font-bold leading-tight ${light ? 'text-white' : 'text-[#1a1a1a]'}`}>
        {heading}
      </h2>
      <div className={`mt-3 w-10 h-1 rounded-full ${light ? 'bg-red-400' : 'bg-[#ED2E24]'}`} />
      {sub && (
        <p className={`mt-4 text-[15px] leading-relaxed max-w-2xl ${light ? 'text-gray-300' : 'text-gray-500'}`}>
          {sub}
        </p>
      )}
    </div>
  );
}

/* ─── Component ────────────────────────────────────────────────────────── */
export default function KyoOwnPage() {
  const navigate = useNavigate();
  const [persona, setPersona]         = useState('parent');
  const [filledDrops, setFilledDrops] = useState(0);
  const [submitted, setSubmitted]     = useState(false);
  const [form, setForm]               = useState({
    name: '', phone: '', parish: '', persona: 'Stay-at-home parent',
  });

  const p = PERSONAS[persona];

  const dropResult = () => {
    if (filledDrops === 0) return '';
    if (filledDrops < 6) return `${6 - filledDrops} ${6 - filledDrops === 1 ? 'delivery' : 'deliveries'} to go.`;
    return 'Your month is paid. Everything else is yours.';
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);
  };

  const scrollTo = (id) => (e) => {
    e.preventDefault();
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Navbar />

      <main className="flex-1 pt-[70px]">

        {/* ── BACK LINK ───────────────────────────────────────────────── */}
        <div className="bg-white border-b border-gray-100">
          <div className="max-w-[1240px] mx-auto px-5 lg:px-6 py-2">
            <button
              onClick={() => navigate('/')}
              className="text-[13px] text-gray-400 hover:text-gray-600 transition-colors flex items-center gap-1"
              data-testid="kyo-own-back-btn"
            >
              ← Back to Homepage
            </button>
          </div>
        </div>

        {/* ── TICKER BAND ─────────────────────────────────────────────── */}
        <div className="bg-[#ED2E24]">
          <div className="max-w-[1240px] mx-auto px-5 lg:px-6 py-2.5 flex items-center gap-3 flex-wrap">
            <span className="w-2 h-2 rounded-full bg-white animate-pulse shrink-0" />
            <p className="text-white text-[12px] font-semibold tracking-wide">
              FOUNDING COHORT — 100 SLOTS &nbsp;·&nbsp; 14 PARISHES &nbsp;·&nbsp; PILOT OPEN NOW: KINGSTON &amp; ST. ANDREW · MANCHESTER · CLARENDON
            </p>
          </div>
        </div>

        {/* ── HERO ────────────────────────────────────────────────────── */}
        <section className="bg-[#212327] text-white">
          <div className="max-w-[1240px] mx-auto px-5 lg:px-6 py-20 lg:py-28">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              <div>
                <p className="text-[12px] font-bold uppercase tracking-widest text-red-400 mb-5">
                  A KYo Group Limited Micro-Franchise Programme
                </p>
                <h1 className="text-4xl sm:text-5xl lg:text-[3.4rem] font-bold leading-[1.08] mb-6">
                  Own the delivery.<br />
                  Own the income.<br />
                  <span className="text-[#ED2E24]">Own your future.</span>
                </h1>
                <p className="text-[16px] text-gray-300 leading-relaxed max-w-lg mb-8">
                  Your neighbourhood already trusts you. KYo.Own gives you the Florida warehouse,
                  customs clearance, dispatch app and brand to turn that trust into a real,
                  protected business — for less than JA$200 a day.
                </p>
                <div className="flex flex-wrap gap-3 mb-10">
                  <a
                    href="#kpo-signup"
                    onClick={scrollTo('kpo-signup')}
                    className="bg-[#ED2E24] hover:bg-[#c9231a] text-white text-[14px] font-semibold px-7 py-3 rounded-full transition-colors shadow-sm"
                    data-testid="hero-cta-btn"
                  >
                    Reserve your founding slot →
                  </a>
                  <a
                    href="https://wa.me/18765423719"
                    target="_blank"
                    rel="noreferrer"
                    className="bg-[#25D366] hover:bg-[#1ea352] text-white text-[14px] font-semibold px-7 py-3 rounded-full transition-colors flex items-center gap-2"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                    Chat on WhatsApp
                  </a>
                </div>
                <div className="flex flex-wrap gap-8 pt-8 border-t border-white/10">
                  {[
                    { val: '100', label: 'Founding partner slots' },
                    { val: '14', label: 'Parishes targeted' },
                    { val: '1st mo. free', label: 'Earn before you pay a cent' },
                  ].map((s) => (
                    <div key={s.val}>
                      <p className="text-2xl font-bold text-[#ED2E24]">{s.val}</p>
                      <p className="text-[13px] text-gray-400 mt-0.5">{s.label}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Parish map card */}
              <div className="rounded-2xl border border-white/10 bg-white/5 p-6 backdrop-blur-sm">
                <svg viewBox="0 0 340 260" width="100%" className="block">
                  <style>{`
                    @keyframes kpo-dash { to { stroke-dashoffset: -400; } }
                    @keyframes kpo-pulse {
                      0%   { opacity: 0.6; transform: scale(0.4); }
                      100% { opacity: 0;   transform: scale(2.4); }
                    }
                    .kpo-route-path {
                      fill: none; stroke: #ED2E24; stroke-width: 2;
                      stroke-dasharray: 6 8; stroke-linecap: round;
                      animation: kpo-dash 24s linear infinite;
                    }
                    .kpo-pulse-ring {
                      fill: none; stroke: #ED2E24; stroke-width: 1.5;
                      opacity: 0; transform-origin: center;
                    }
                    .n1 .kpo-pulse-ring { animation: kpo-pulse 3s ease-out infinite; }
                    .n2 .kpo-pulse-ring { animation: kpo-pulse 3s ease-out infinite 1s; }
                    .n3 .kpo-pulse-ring { animation: kpo-pulse 3s ease-out infinite 2s; }
                  `}</style>
                  <path className="kpo-route-path" d="M30,210 C 80,150 60,90 120,70 S 220,40 190,110 S 260,150 300,60" />
                  <g className="n1">
                    <circle cx="30" cy="210" r="5" fill="white" />
                    <circle cx="30" cy="210" r="5" className="kpo-pulse-ring" />
                  </g>
                  <g className="n2">
                    <circle cx="190" cy="110" r="5" fill="white" />
                    <circle cx="190" cy="110" r="5" className="kpo-pulse-ring" />
                  </g>
                  <g className="n3">
                    <circle cx="300" cy="60" r="6" fill="#ED2E24" />
                    <circle cx="300" cy="60" r="6" className="kpo-pulse-ring" />
                  </g>
                  <text x="10" y="230" style={{ fontFamily: 'inherit', fontSize: '9px', fill: 'rgba(255,255,255,0.75)' }}>CLARENDON</text>
                  <text x="150" y="132" style={{ fontFamily: 'inherit', fontSize: '9px', fill: 'rgba(255,255,255,0.75)' }}>MANCHESTER</text>
                  <text x="200" y="45" style={{ fontFamily: 'inherit', fontSize: '9px', fill: 'rgba(255,255,255,0.75)' }}>KINGSTON &amp; ST. ANDREW</text>
                </svg>
                <p className="text-[13px] text-gray-400 mt-4 leading-relaxed">
                  Three pilot parishes today. Six have no named courier at all — one could be yours.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* ── MANIFESTO ───────────────────────────────────────────────── */}
        <section className="bg-gray-50 py-16 lg:py-20">
          <div className="max-w-[860px] mx-auto px-5 lg:px-6 text-center">
            <div className="w-8 h-0.5 bg-[#ED2E24] mx-auto mb-8" />
            <blockquote className="text-2xl sm:text-3xl lg:text-[2rem] font-bold text-[#1a1a1a] leading-snug">
              "Because logistics is not about trucks. It is about trust."
            </blockquote>
            <p className="mt-5 text-[13px] font-semibold uppercase tracking-widest text-gray-400">
              — The KYo.Own Manifesto
            </p>
          </div>
        </section>

        {/* ── WHO IT'S FOR ────────────────────────────────────────────── */}
        <section id="kpo-who" className="py-20 lg:py-24 px-5 lg:px-6">
          <div className="max-w-[1240px] mx-auto">
            <SectionHead
              eyebrow="Who it's for"
              heading="Whoever you are, your network is already worth something."
              sub="KYo.Own isn't one pitch — it's six. Select the one that sounds like you."
            />

            {/* Persona tabs */}
            <div className="flex flex-wrap gap-2.5 mb-8" role="tablist">
              {PERSONA_TABS.map((t) => (
                <button
                  key={t.key}
                  role="tab"
                  aria-selected={persona === t.key}
                  onClick={() => setPersona(t.key)}
                  data-testid={`persona-tab-${t.key}`}
                  className={`text-[13px] font-semibold px-4 py-2 rounded-full border transition-colors ${
                    persona === t.key
                      ? 'bg-[#ED2E24] border-[#ED2E24] text-white'
                      : 'border-gray-200 text-gray-600 hover:border-gray-400 bg-white'
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>

            {/* Persona panel */}
            <div className="rounded-2xl border border-gray-100 shadow-sm overflow-hidden bg-white">
              <div className="grid md:grid-cols-2 gap-0">
                <div className="p-8 border-b md:border-b-0 md:border-r border-gray-100">
                  <h3 className="text-[22px] font-bold text-[#1a1a1a] leading-snug mb-4">{p.headline}</h3>
                  <p className="text-[15px] text-gray-600 leading-relaxed">{p.copy}</p>
                </div>
                <div className="p-8 flex flex-col justify-between bg-gray-50">
                  <blockquote className="text-[15px] text-gray-700 italic leading-relaxed border-l-2 border-[#ED2E24] pl-4 mb-6">
                    {p.quote}
                  </blockquote>
                  <div>
                    <p className="text-2xl font-bold text-[#ED2E24]">{p.stat}</p>
                    <p className="text-[12px] text-gray-400 mt-1">{p.statLabel}</p>
                  </div>
                </div>
              </div>
            </div>
            <p className="text-[12px] text-gray-400 mt-4 leading-relaxed">
              *Illustrative example based on current freight, service and pickup fee splits.
              Earnings claims here are illustrative, not guaranteed — actual results depend on
              volume, area and hours worked. See the real math on the US$35 fee below.
            </p>
          </div>
        </section>

        {/* ── SIX DELIVERIES ──────────────────────────────────────────── */}
        <section id="kpo-thirty-five" className="bg-[#1a1a1a] py-20 lg:py-24 px-5 lg:px-6">
          <div className="max-w-[1240px] mx-auto">
            <SectionHead
              eyebrow="The real math"
              heading="US$35 a month isn't a fee to work. It's what six deliveries pays for."
              sub="You're not competing against free delivery — you're skipping the cost of building your own Florida warehouse arrangement, customs broker, dispatch software and insurance. Nobody does that for JA$5,500 a month."
              light
            />

            <div className="inline-flex items-center gap-2 bg-[#ED2E24] text-white text-[13px] font-semibold px-5 py-2.5 rounded-full mb-8">
              First month free — earn before you pay a cent
            </div>

            {/* Counter */}
            <div className="flex flex-wrap gap-3 mb-6">
              {[1, 2, 3, 4, 5, 6].map((n) => (
                <button
                  key={n}
                  onClick={() => setFilledDrops(n)}
                  data-testid={`delivery-drop-${n}`}
                  className={`w-14 h-14 rounded-full border-2 font-bold text-[18px] transition-all ${
                    filledDrops >= n
                      ? 'bg-[#ED2E24] border-[#ED2E24] text-white shadow-lg'
                      : 'border-dashed border-white/25 text-white/40 hover:border-white/50'
                  }`}
                >
                  {n}
                </button>
              ))}
            </div>
            <p className="text-[15px] text-gray-300 max-w-xl mb-3">
              Tap each drop. At JA$800–1,200 per delivery, six of them covers your whole month — every delivery after that is yours to keep.
            </p>
            {filledDrops > 0 && (
              <p className="text-[18px] font-bold text-[#ED2E24] mb-8" data-testid="six-result">
                {dropResult()}
              </p>
            )}

            {/* Reassurance cards */}
            <div className="grid sm:grid-cols-3 gap-4 mt-8">
              {[
                { title: 'No buy-in fee', body: 'No franchise purchase price. Just the monthly platform fee, waived in month one.' },
                { title: 'No recruitment bonus', body: 'You earn from deliveries only — never from signing up other partners.' },
                { title: 'Fixed for 12 months', body: 'US$35 is locked in Jamaican dollars for your first year — no surprise FX increases.' },
              ].map((c) => (
                <div key={c.title} className="rounded-xl border border-white/10 bg-white/5 p-5">
                  <p className="text-[15px] font-semibold text-white mb-1.5">{c.title}</p>
                  <p className="text-[13px] text-gray-400 leading-relaxed">{c.body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── FEE BREAKDOWN ───────────────────────────────────────────── */}
        <section className="py-20 lg:py-24 px-5 lg:px-6">
          <div className="max-w-[1240px] mx-auto">
            <SectionHead
              eyebrow="What your $35 actually covers"
              heading="Published in full. No hidden line items."
            />
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              {[
                { icon: '🏭', title: 'Florida warehouse & freight', desc: 'Receiving, storage and shipping of every customer package to Jamaica.' },
                { icon: '🛃', title: 'Customs clearance', desc: 'Duty processing and compliance handled on every shipment, automatically.' },
                { icon: '📲', title: 'KANAL dispatch app', desc: 'Your earnings dashboard, tracking, customer notifications and weekly payouts.' },
                { icon: '🏷️', title: 'Your brand & training', desc: 'Branded storefront, marketing kit, onboarding and ongoing partner support.' },
              ].map((item) => (
                <div key={item.title} className="rounded-2xl border border-gray-100 bg-gray-50 p-6">
                  <span className="text-2xl mb-3 block">{item.icon}</span>
                  <h3 className="text-[15px] font-semibold text-[#1a1a1a] mb-2">{item.title}</h3>
                  <p className="text-[13px] text-gray-500 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
            <div className="rounded-2xl bg-[#1a1a1a] px-8 py-6 flex flex-wrap items-center justify-between gap-4">
              <div>
                <p className="text-3xl font-bold text-white">
                  US$35<span className="text-base font-normal text-gray-400 ml-1">/month</span>
                </p>
                <p className="text-[13px] text-gray-400 mt-1">≈ JA$5,500 · fixed for 12 months</p>
              </div>
              <p className="text-[14px] text-gray-300 max-w-sm leading-relaxed">
                Less than JA$200 a day to run a business that would otherwise cost you a warehouse arrangement, a customs broker and dispatch software to build alone.
              </p>
            </div>
          </div>
        </section>

        {/* ── TERRITORY ───────────────────────────────────────────────── */}
        <section className="bg-[#ED2E24] py-20 lg:py-24 px-5 lg:px-6">
          <div className="max-w-[1240px] mx-auto">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <p className="text-[12px] font-bold uppercase tracking-widest text-red-200 mb-4">Territory exclusivity</p>
                <h2 className="text-3xl sm:text-4xl font-bold text-white leading-tight mb-4">
                  Your parish. Protected.
                </h2>
                <div className="w-10 h-1 rounded-full bg-white/40 mb-6" />
                <p className="text-[16px] text-red-100 leading-relaxed max-w-md">
                  Founding KYo.Own partners operate a defined zone within their parish — your
                  neighbourhood, your customer base, without a second KYo.Own partner recruited into
                  your area while you're active. Nobody else in Jamaican logistics offers this.
                </p>
              </div>
              <div className="flex flex-col items-center text-center bg-white/10 rounded-2xl p-10 border border-white/20">
                <p className="text-6xl font-bold text-white">1 / 100</p>
                <p className="text-[14px] text-red-100 mt-4 max-w-xs leading-relaxed">
                  Founding slots are parish-limited — once your area is claimed, it's closed.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* ── PROMISES ────────────────────────────────────────────────── */}
        <section id="kpo-promises" className="py-20 lg:py-24 px-5 lg:px-6">
          <div className="max-w-[1240px] mx-auto">
            <SectionHead
              eyebrow="The owner promises"
              heading="Six things we owe every partner, in writing."
            />
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {[
                { num: '01', title: 'You own the business',   body: 'Your branded storefront, your customers, your income. KYo is your backbone, not your boss.' },
                { num: '02', title: 'You own your community', body: 'You know your street better than any logistics company. We just give it a technology layer.' },
                { num: '03', title: 'You own your time',      body: 'No shifts, no clock-in, no manager. You decide when you work and how fast you grow.' },
                { num: '04', title: 'You own your earnings',  body: 'Transparent weekly payouts. No hidden fees. Every fee and bonus itemised in the app.' },
                { num: '05', title: 'You own your growth',    body: 'Start with 10 customers, scale to 200. Tiered earnings reward every step up.' },
                { num: '06', title: 'You are never alone',    body: 'Freight, customs, warehousing and last-mile via ZipExpress — all handled behind you.' },
              ].map((pr) => (
                <div
                  key={pr.num}
                  data-testid={`promise-${pr.num}`}
                  className="rounded-2xl border border-gray-100 bg-white p-6 hover:shadow-md transition-shadow"
                >
                  <span className="text-[11px] font-bold uppercase tracking-widest text-[#ED2E24] block mb-3">
                    Promise {pr.num}
                  </span>
                  <h3 className="text-[16px] font-semibold text-[#1a1a1a] mb-2">{pr.title}</h3>
                  <p className="text-[13px] text-gray-500 leading-relaxed">{pr.body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── HOW IT WORKS ────────────────────────────────────────────── */}
        <section id="kpo-how" className="bg-gray-50 py-20 lg:py-24 px-5 lg:px-6">
          <div className="max-w-[1240px] mx-auto">
            <SectionHead
              eyebrow="How it works"
              heading="Three steps between today and your first payout."
            />
            <div className="grid md:grid-cols-3 gap-6 relative">
              {/* connector line */}
              <div className="hidden md:block absolute top-6 left-[calc(16.66%+12px)] right-[calc(16.66%+12px)] h-0.5 bg-gray-200" />
              {[
                { n: '1', title: 'Sign up', body: 'Ten minutes on KYoOwn.com. Tell us your parish and how much time you have to give.' },
                { n: '2', title: 'Get branded & trained', body: 'Storefront, app access and starter training modules — live within 24 hours.' },
                { n: '3', title: 'Start earning — free for month one', body: 'Take your first pickups. Weekly payouts land straight in the app, itemised. Your US$35 starts month two.' },
              ].map((step) => (
                <div key={step.n} className="relative">
                  <div className="w-12 h-12 rounded-full bg-[#ED2E24] text-white font-bold text-[18px] flex items-center justify-center mb-5 shadow-md relative z-10">
                    {step.n}
                  </div>
                  <h3 className="text-[17px] font-semibold text-[#1a1a1a] mb-2">{step.title}</h3>
                  <p className="text-[14px] text-gray-500 leading-relaxed">{step.body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── OBJECTIONS ──────────────────────────────────────────────── */}
        <section className="py-20 lg:py-24 px-5 lg:px-6">
          <div className="max-w-[1240px] mx-auto">
            <SectionHead
              eyebrow="Before you ask"
              heading="The questions everyone asks about the $35."
            />
            <div className="grid sm:grid-cols-2 gap-4">
              {[
                { q: '"Why am I paying to work?"',    a: <>You're not paying to work. <strong className="text-[#ED2E24]">You're paying to own.</strong></> },
                { q: '"Others deliver free"',          a: <>They deliver free because you're the customer. Here, <strong className="text-[#ED2E24]">you're the business.</strong></> },
                { q: '"What if I don\'t earn?"',       a: <><strong className="text-[#ED2E24]">First month free.</strong> Earn before you pay a cent.</> },
                { q: '"It sounds like a scheme"',      a: <><strong className="text-[#ED2E24]">No buy-in. No recruitment bonus.</strong> You earn from deliveries only.</> },
                { q: '"Can I afford it?"',             a: <><strong className="text-[#ED2E24]">Less than JA$200 a day</strong> to run a real business.</> },
                { q: '"What am I paying for?"',        a: <>Florida warehouse, customs, dispatch app, <strong className="text-[#ED2E24]">your brand, your training.</strong></> },
              ].map((obj, i) => (
                <div key={i} className="rounded-xl border border-gray-100 bg-gray-50 px-6 py-5">
                  <p className="text-[15px] font-semibold text-[#1a1a1a] mb-1.5">{obj.q}</p>
                  <p className="text-[14px] text-gray-600 leading-relaxed">{obj.a}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── SIGNUP ──────────────────────────────────────────────────── */}
        <section id="kpo-signup" className="bg-[#1a1a1a] py-20 lg:py-24 px-5 lg:px-6">
          <div className="max-w-[1240px] mx-auto">
            <div className="grid md:grid-cols-2 gap-12 lg:gap-16 items-start">
              {/* Left copy */}
              <div>
                <p className="text-[12px] font-bold uppercase tracking-widest text-red-400 mb-4">Ready to own your KYo?</p>
                <h2 className="text-3xl sm:text-4xl font-bold text-white leading-tight mb-4">
                  Join the founding cohort.
                </h2>
                <div className="w-10 h-1 rounded-full bg-[#ED2E24] mb-6" />
                <p className="text-[15px] text-gray-300 leading-relaxed mb-8 max-w-md">
                  100 slots across 14 parishes. Pilot is open now in Kingston &amp; St. Andrew, Manchester and Clarendon.
                </p>
                <div className="rounded-xl border border-white/10 bg-white/5 p-5 mb-6">
                  <p className="text-2xl font-bold text-white">
                    US$35<span className="text-sm font-normal text-gray-400 ml-1">/mo</span>
                  </p>
                  <p className="text-[13px] text-gray-400 mt-1">≈ JA$5,500 · fixed 12 months · first month free · no buy-in</p>
                </div>
                <a
                  href="https://wa.me/18765423719"
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#1ea352] text-white text-[14px] font-semibold px-6 py-3 rounded-full transition-colors"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                  Or just message us on WhatsApp
                </a>
              </div>

              {/* Form card */}
              <div className="rounded-2xl bg-white p-8 shadow-xl">
                {!submitted ? (
                  <>
                    <h3 className="text-[20px] font-bold text-[#1a1a1a] mb-1">Start your business</h3>
                    <p className="text-[13px] text-gray-400 mb-6">First month free. No buy-in. No commitment beyond that.</p>
                    <form onSubmit={handleSubmit} data-testid="kyo-own-form" className="space-y-4">
                      <div>
                        <label htmlFor="kpo-name" className="block text-[13px] font-semibold text-gray-700 mb-1.5">Full name</label>
                        <input
                          type="text" id="kpo-name" required placeholder="Your name"
                          value={form.name}
                          onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                          className="w-full border border-gray-200 rounded-xl px-4 py-3 text-[14px] text-gray-900 bg-white outline-none focus:border-[#ED2E24] transition-colors"
                        />
                      </div>
                      <div>
                        <label htmlFor="kpo-phone" className="block text-[13px] font-semibold text-gray-700 mb-1.5">Phone / WhatsApp</label>
                        <input
                          type="tel" id="kpo-phone" required placeholder="876-000-0000"
                          value={form.phone}
                          onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                          className="w-full border border-gray-200 rounded-xl px-4 py-3 text-[14px] text-gray-900 bg-white outline-none focus:border-[#ED2E24] transition-colors"
                        />
                      </div>
                      <div>
                        <label htmlFor="kpo-parish" className="block text-[13px] font-semibold text-gray-700 mb-1.5">Parish</label>
                        <select
                          id="kpo-parish" required
                          value={form.parish}
                          onChange={(e) => setForm((f) => ({ ...f, parish: e.target.value }))}
                          className="w-full border border-gray-200 rounded-xl px-4 py-3 text-[14px] text-gray-900 bg-white outline-none focus:border-[#ED2E24] transition-colors"
                        >
                          <option value="">Select your parish</option>
                          <option>Kingston &amp; St. Andrew (pilot)</option>
                          <option>Manchester — Mandeville / Christiana (pilot)</option>
                          <option>Clarendon — May Pen (pilot)</option>
                          <option>Other parish — join early interest list</option>
                        </select>
                      </div>
                      <div>
                        <label htmlFor="kpo-persona" className="block text-[13px] font-semibold text-gray-700 mb-1.5">Which sounds like you?</label>
                        <select
                          id="kpo-persona"
                          value={form.persona}
                          onChange={(e) => setForm((f) => ({ ...f, persona: e.target.value }))}
                          className="w-full border border-gray-200 rounded-xl px-4 py-3 text-[14px] text-gray-900 bg-white outline-none focus:border-[#ED2E24] transition-colors"
                        >
                          <option>Stay-at-home parent</option>
                          <option>Taxi / route driver</option>
                          <option>Teacher / civil servant</option>
                          <option>Young entrepreneur / influencer</option>
                          <option>Retiree</option>
                          <option>Hairdresser / nail tech</option>
                          <option>Something else</option>
                        </select>
                      </div>
                      <button
                        type="submit"
                        className="w-full bg-[#ED2E24] hover:bg-[#c9231a] text-white text-[14px] font-semibold py-3.5 rounded-full transition-colors mt-2"
                        data-testid="kyo-own-submit"
                      >
                        Reserve my founding slot →
                      </button>
                    </form>
                    <p className="text-[11px] text-gray-400 text-center mt-4">
                      By signing up you'll receive a WhatsApp message to confirm your parish slot.
                    </p>
                  </>
                ) : (
                  <div className="text-center py-6" data-testid="kyo-own-success">
                    <div className="w-14 h-14 rounded-full bg-[#ED2E24] text-white text-2xl flex items-center justify-center mx-auto mb-5">
                      ✓
                    </div>
                    <h3 className="text-[20px] font-bold text-[#1a1a1a] mb-2">You're on the list.</h3>
                    <p className="text-[14px] text-gray-500 leading-relaxed">
                      We'll reach out on WhatsApp within 48 hours to confirm your parish slot and get your storefront started.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>

      </main>

      <Footer />
    </div>
  );
}
